import { Activity, Plane, ShieldPlus, type LucideIcon } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { remindTonight } from "../../services/api/nudgeApi";
import { useI18nStore } from "../../stores/i18nStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import type { Translation } from "@/types/i18n";
import { bloodPressureStatus, readingAge, type NextStep } from "./homeLogic";
import { fill } from "./fill";
import { ageText, statusText } from "./vitalText";

interface Props {
  step: NextStep;
  onDismiss: () => void;
  now: Date;
}

interface StepView {
  icon: LucideIcon;
  title: string;
  body: string;
  action: string;
  to: string;
}

function view(step: NextStep, t: Translation, now: Date): StepView {
  switch (step.id) {
    case "emergency":
      return {
        icon: ShieldPlus,
        title: t.stepEmergencyTitle,
        body: t.stepEmergencyBody,
        action: t.stepEmergencyAction,
        to: "/traveler/health/emergency",
      };
    case "bp": {
      const { systolic, diastolic, recordedAt } = step.reading;
      const value = `${systolic}/${diastolic}`;
      const body =
        step.reason === "abnormal"
          ? fill(t.stepBpBodyAbnormal, {
              value,
              status: statusText(
                bloodPressureStatus(systolic, diastolic),
                t,
              ).toLowerCase(),
            })
          : readingAge(recordedAt, now).kind === "unknown"
            ? fill(t.stepBpBodyUndated, { value })
            : fill(t.stepBpBodyStale, {
                value,
                age: ageText(readingAge(recordedAt, now), t),
              });
      return {
        icon: Activity,
        title: t.stepBpTitle,
        body,
        action: t.stepBpAction,
        to: "/traveler/health",
      };
    }
    case "trip":
      return {
        icon: Plane,
        title: fill(t.stepTripTitle, { destination: step.destination }),
        body: t.stepTripBody,
        action: t.stepTripAction,
        to: `/traveler/travel/${step.tripId}/readiness`,
      };
    case "plan":
      return {
        icon: Plane,
        title: t.stepPlanTitle,
        body: t.stepPlanBody,
        action: t.homePlanTrip,
        to: "/traveler/travels/new",
      };
  }
}

/** The single nudge on Home (UX_CONCEPT.md §6.2): one action, always dismissible. */
export const NextBestStepCard: React.FC<Props> = ({ step, onDismiss, now }) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const v = view(step, t, now);
  const Icon = v.icon;
  const [reminder, setReminder] = useState<
    { state: "idle" | "saving" | "failed" } | { state: "set"; at: Date }
  >({ state: "idle" });

  const remind = async () => {
    setReminder({ state: "saving" });
    try {
      const at = await remindTonight(
        step.id,
        step.id === "trip" ? step.tripId : undefined,
      );
      setReminder({ state: "set", at });
    } catch {
      setReminder({ state: "failed" });
    }
  };

  const reminderLabel =
    reminder.state === "set"
      ? fill(t.homeReminderSet, {
          time: fmt.time(reminder.at),
        })
      : reminder.state === "failed"
        ? t.homeReminderFailed
        : t.homeRemindTonight;

  return (
    <section
      aria-label={t.homeNextStep}
      className="rounded-[22px] bg-white p-5 shadow-sm"
    >
      <div className="text-[11px] font-bold tracking-[0.14em] text-travel-600">
        {t.homeNextStep.toUpperCase()}
      </div>
      <div className="mt-3 flex gap-3.5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-travel-100 text-travel-600">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold leading-snug text-ink">
            {v.title}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {v.body}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5">
        <Link
          to={v.to}
          className="flex min-h-[48px] flex-[1.4_1_160px] items-center justify-center rounded-[14px] bg-ink px-4 text-center text-[15px] font-bold text-white"
        >
          {v.action}
        </Link>
        <button
          type="button"
          onClick={() => {
            // Not `disabled`: the global disabled style fades the button,
            // and "Reminder set" is the one thing it should say clearly.
            if (reminder.state !== "saving" && reminder.state !== "set") {
              void remind();
            }
          }}
          aria-disabled={
            reminder.state === "saving" || reminder.state === "set"
          }
          aria-live="polite"
          className={`min-h-[48px] flex-[1_1_140px] rounded-[14px] border border-input bg-white px-4 text-sm font-semibold ${
            reminder.state === "set" ? "text-health-700" : "text-ink"
          }`}
        >
          {reminderLabel}
        </button>
      </div>
      <div className="mt-1 flex justify-end">
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-[44px] px-2 text-sm font-semibold text-muted-foreground hover:text-ink"
        >
          {t.homeNotNow}
        </button>
      </div>
    </section>
  );
};
